// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AdminStats.js
// Admin: AI Stats - how the new-system announcement ratings turned out against the price.
// Data: /admin/stats/rows (admin.py stats_rows) - every summary with an expected move (the new
// system, from 23 Oct 2026) released in the date range, with its outcome: the total reaction
// (last price before release -> close of the first session after it, pre/post-market and the
// ASX auction included - what the band predicts) and the move after the entry (what a trade
// at the AI Audit page's entry got). No AI calls; everything below is worked out in the browser.
//   Direction and band are scored together as one signed call: positive 10-20% = +10 to +20%;
//   mixed and neutral = a call that it stays inside ±2%. Each row gets an outcome tier, best to worst:
//   spot on / one band off / 2+ bands off (right way) / stayed flat / wrong way, and points
//   (100 / 75 / 50 / 25 / 0) averaged into a score out of 100. Wrong way = moved 2%+ against a
//   positive / negative call, or any move against it for a <2% call (small calls are scored on sign).
//   Stayed flat = a 2%+ positive / negative call that ended inside ±2%, or a <2% one that closed unchanged.
//   Filters: date range (with quick buttons), exchange, direction (tick any), minimum significance.
//   One row per company per day (default): a company's documents moving on the same session
//   count once, as its highest-rated document (significance, then band) - switch to every
//   announcement. Move: total reaction (default) or after entry.
//   Doughnuts: outcome tiers, and right way vs wrong way. Tiles: score, direction, spot on, wrong
//   way, 10%+ calls that came true, big movers caught.
//   Heatmap: signed call (rows) x signed move (columns), shaded by the share of the row - blue,
//   or red where it went the wrong way; spot-on cells outlined; positive calls on top. Click cells in one row
//   to select them (another row starts a new selection): combined count and share of that call, listed below.
//   Outcome by call and size check bars, small (<2%) calls, score by week, breakdown by sector,
//   group, commodity, type, company and more (click to drill in). Wrong way, missed movers and
//   false alarms below. Colours: dataviz skill palette checker - tier set passes CVD and
//   normal-vision separation in both modes (grey midpoint and the light blue step carry visible
//   labels); heatmap ramps are one-hue light->dark. Text stays in text colours.
//   What the Logic Tree uses (/admin/stats/logic-tree-inputs): the live model's share of skill per input
//   family for a 5%+ rise / fall (AUC lost when scrambled on held-out weeks, as % of AUC above 0.5), its top
//   single inputs, and each family's share across the last 8 models (does a new input start to matter?).
// Admin only (guard as AdminAudit.js); the API is @admin_required.
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

const MOVES = ['<2', '2-5', '5-10', '10-20', '20-50', '50+'];            // bands, low to high
const BIG = ['10-20', '20-50', '50+'];                                   // 10%+
const BIG5 = ['5-10', '10-20', '20-50', '50+'];                           // 5%+ (the reaction model's alert target)
const DIRS = ['positive', 'mixed', 'negative', 'neutral'];
const SIGNS = { positive: 1, negative: -1 };                             // mixed / neutral = a call for under 2%
const START = '2026-10-23';                                             // first new-system summaries
const TIERS = [['spot', 'Spot on'], ['close', 'One band off'], ['off', '2+ bands off'], ['flat', 'Stayed flat'], ['wrong', 'Wrong way']];
const TIER_LABEL = Object.fromEntries(TIERS);
const POINTS = { spot: 100, close: 75, off: 50, flat: 25, wrong: 0 };
const NEG = (s, b) => `${s < 0 ? '−' : '+'}${b}`;
const CALLS = [...[...MOVES].reverse().map(b => NEG(1, b)), 'flat', ...MOVES.map(b => NEG(-1, b))];   // rows, positive on top
const COLS = [...[...MOVES].reverse().map(b => NEG(-1, b)), ...MOVES.map(b => NEG(1, b))];              // heatmap columns
const SOURCES = { asx: 'ASX', sec: 'EDGAR', bw: 'Business Wire', gnw: 'GlobeNewswire', prn: 'PR Newswire',
                  anw: 'ACCESS Newswire', nfc: 'Newsfile', dod: 'Dept of War', insider: 'Insider buying' };
const DIMS = [                                                          // breakdown / drill dimensions
  { key: 'sector', label: 'Sector', get: r => [r.sector] },
  { key: 'group', label: 'Industry group', get: r => [r.group] },
  { key: 'commodity', label: 'Commodity', get: r => r.commodities.length ? r.commodities : ['-'] },
  { key: 'type', label: 'Announcement type', get: r => [SOURCES[r.type] || r.type] },
  { key: 'exchange', label: 'Exchange', get: r => [r.exchange] },
  { key: 'significance', label: 'Importance', get: r => [String(r.significance)] },
  { key: 'direction', label: 'Direction', get: r => [r.direction || '-'] },
  { key: 'call', label: 'Call', get: r => [r.call] },
  { key: 'tier', label: 'Outcome', get: r => [TIER_LABEL[r.tier]] },
  { key: 'novelty', label: 'Novelty', get: r => [r.novelty || '-'] },
  { key: 'surprise', label: 'Surprise', get: r => [r.surprise || '-'] },
  { key: 'given', label: 'Band given', get: r => [r.expected_move] },
  { key: 'actual', label: 'Band moved', get: r => [r.actual] },
  { key: 'company', label: 'Company', get: r => [r.ticker] },
];

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '32px 16px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px', maxWidth: '900px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px', marginBottom: '24px', minWidth: 0 },
  title:   { fontSize: '14px', fontWeight: 600, marginBottom: '10px' },
  filters: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' },
  label:   { fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' },
  input:   { padding: '5px 8px', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", border: '1px solid var(--border)',
             borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  btn:     { padding: '4px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', cursor: 'pointer',
             fontSize: '12px', fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)', color: 'var(--text)' },
  on:      { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' },
  chip:    { display: 'inline-flex', gap: '6px', alignItems: 'center', padding: '2px 8px', borderRadius: '999px', fontSize: '12px',
             background: 'var(--accent-dim)', color: 'var(--accent)', marginRight: '6px', cursor: 'pointer' },
  scroll:  { overflowX: 'auto' },
  table:   { borderCollapse: 'collapse', fontSize: '12px', width: '100%' },
  th:      { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 500,
             fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td:      { padding: '6px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  num:     { fontFamily: "'DM Mono', monospace", textAlign: 'right', whiteSpace: 'nowrap' },
  muted:   { color: 'var(--text-muted)' },
  link:    { color: 'var(--accent)', cursor: 'pointer' },
  err:     { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  mono:    { fontFamily: "'DM Mono', monospace" },
};

const C = {                                                             // data colours (validated)
  blue: ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#104281'],   // sequential: share of row
  red:  ['#f4b8b7', '#ec8584', '#e05453', '#c33b3a', '#9a2a29', '#741c1b'],   // sequential: share of row, wrong way
  low2: '#1c5cab', low: '#5598e7', right: '#8f8d86', high: '#ec6f6e', high2: '#b93433',  // diverging
  spot: '#184f95', close: '#3987e5', off: '#86b6ef', flat: '#8f8d86', wrong: '#b93433',  // outcome tiers
  track: '#ECECE8', meter: '#2a78d6',
};
const shade = (f, ramp) => (f <= 0 ? 'transparent' : ramp[Math.min(ramp.length - 1, Math.floor(f * ramp.length))]);

const bandOf = p => { const a = Math.abs(p); return a < 2 ? '<2' : a < 5 ? '2-5' : a < 10 ? '5-10' : a < 20 ? '10-20' : a < 50 ? '20-50' : '50+'; };
const iso = d => d.toISOString().slice(0, 10);
const daysAgo = n => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : '-');
const fmt = v => (v == null ? '-' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
const avg = xs => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const weekOf = d => { const t = new Date(`${d}T00:00:00Z`); t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7)); return iso(t); };

// Outcome tier of one row (see the header).
const tier = r => {
  const sg = SIGNS[r.direction] || 0, gi = MOVES.indexOf(r.expected_move), ai = MOVES.indexOf(r.actual);
  if (sg && r.move * sg < 0 && (ai > 0 || gi === 0)) return 'wrong';
  if (sg && ai === 0 && (gi > 0 || r.move === 0)) return 'flat';        // unchanged close: neither way
  const d = sg ? Math.abs(gi - ai) : ai;
  return d === 0 ? 'spot' : d === 1 ? 'close' : 'off';
};

// Scores for a set of rows - used by the headline and every breakdown row.
function score(rs) {
  const gi = r => MOVES.indexOf(r.expected_move), ai = r => MOVES.indexOf(r.actual);
  const said = rs.filter(r => BIG.includes(r.expected_move)), moved = rs.filter(r => BIG.includes(r.actual));
  const called = rs.filter(r => SIGNS[r.direction] && r.tier !== 'flat');            // calls with a way to be right or wrong
  const t = Object.fromEntries(TIERS.map(([k]) => [k, rs.filter(r => r.tier === k).length]));
  return {
    n: rs.length, t,
    points: rs.length ? rs.reduce((a, r) => a + POINTS[r.tier], 0) / rs.length : null,
    right: rs.filter(r => gi(r) === ai(r)).length,
    near: rs.filter(r => Math.abs(gi(r) - ai(r)) <= 1).length,
    high: rs.filter(r => gi(r) > ai(r)).length,
    low: rs.filter(r => gi(r) < ai(r)).length,
    said: said.length, saidHit: said.filter(r => BIG.includes(r.actual)).length,
    saidFive: said.filter(r => Math.abs(r.move) >= 5).length, saidFlat: said.filter(r => r.actual === '<2').length,
    moved: moved.length, movedCaught: moved.filter(r => BIG.includes(r.expected_move)).length,
    movedFive: moved.filter(r => ['5-10', ...BIG].includes(r.expected_move)).length,
    movedLow: moved.filter(r => r.expected_move === '<2').length,
    movedDir: moved.filter(r => SIGNS[r.direction] && r.tier !== 'wrong').length,
    called: called.length, calledRight: called.filter(r => r.tier !== 'wrong').length,
  };
}

// A headline number with a meter bar.
function Tile({ label, value, of, sub, show }) {
  const f = of ? value / of : null;
  return (
    <div style={{ flex: '1 1 180px', minWidth: 0, background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'calc(var(--radius) * 2)', padding: '14px 16px' }}>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: '30px', fontWeight: 600, letterSpacing: '-0.02em', margin: '4px 0 8px' }}>
        {f == null ? '-' : show || `${Math.round(f * 100)}%`}</div>
      <div style={{ height: '6px', background: C.track, borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ width: `${(f || 0) * 100}%`, height: '100%', background: C.meter, borderRadius: '3px' }} /></div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>{sub}</div>
    </div>
  );
}

// A small bar for a share, with its % written beside it (text in text colour).
// What the Logic Tree leans on: share of the model's skill (AUC above a coin flip) lost when an input is scrambled.
function LogicTreeInputs({ models }) {
  if (!models) return null;
  const share = v => v == null ? '-' : `${v > 0 ? '' : v < 0 ? '−' : ''}${Math.abs(v) < 1 && v !== 0 ? Math.abs(v).toFixed(1) : Math.round(Math.abs(v))}%`;
  const bar = v => <Bar f={v == null ? null : Math.max(0, Math.min(1, v / 100))} text={share(v)} />;
  if (!models.length) return (
    <div style={s.section}><div style={s.title}>What the Logic Tree uses</div>
      <div style={{ ...s.muted, fontSize: '12px' }}>No model with an importance table yet - it comes with each retrain from v9.</div></div>);
  const m = models[0], fam = n => k => (m[k].families.find(f => f.name === n) || {}).share;
  const names = m.up5.families.map(f => f.name);
  const old = [...models].reverse();
  return (
    <div style={s.section}>
      <div style={s.title}>What the Logic Tree uses - model v{m.version}{m.live ? ' (live)' : ''}, trained {m.trained_at.slice(0, 10)}</div>
      <div style={{ ...s.muted, fontSize: '12px', marginBottom: '10px' }}>
        How much of the model's skill each kind of input carries, tested on the weeks it never trained on: scramble the input and see how much
        worse it ranks the announcements that rose (fell) 5%+. Skill = AUC above 0.5, a coin flip (rise {m.up5.auc?.toFixed(3)}, fall {m.down5.auc?.toFixed(3)}).
        0% = the model ignores it; below 0 = noise. Inputs overlap, so the shares needn't add to 100%.
      </div>
      <div style={s.scroll}>
        <table style={s.table}>
          <thead><tr>{[['Input family', 'left'], ['Inputs', 'right'], ['Rise 5%+', 'right'], ['Fall 5%+', 'right']]
            .map(([h, a]) => <th key={h} style={{ ...s.th, textAlign: a }}>{h}</th>)}</tr></thead>
          <tbody>{names.map(n => (
            <tr key={n}>
              <td style={s.td}>{n}</td>
              <td style={s.num}>{(m.up5.families.find(f => f.name === n) || {}).inputs}</td>
              <td style={s.num}>{bar(fam(n)('up5'))}</td>
              <td style={s.num}>{bar(fam(n)('down5'))}</td>
            </tr>))}</tbody>
        </table>
      </div>
      {['up5', 'down5'].map(k => (
        <div key={k} style={{ ...s.muted, fontSize: '12px', marginTop: '8px' }}>
          <b>Top inputs, {k === 'up5' ? 'rise' : 'fall'} 5%+:</b> {m[k].top.filter(t => t.share > 0).slice(0, 10).map(t => `${t.label} ${share(t.share)}`).join(' · ') || '-'}
        </div>))}
      {old.length > 1 && (
        <div style={{ ...s.scroll, marginTop: '12px' }}>
          <div style={{ ...s.muted, fontSize: '12px', marginBottom: '6px' }}>Share of skill by model (rise / fall), oldest first:</div>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Input family</th>{old.map(x => (
              <th key={x.version} style={{ ...s.th, textAlign: 'right' }} title={x.note || ''}>v{x.version}<div style={{ fontWeight: 400 }}>{x.trained_at.slice(5, 10)}</div></th>))}</tr></thead>
            <tbody>{names.map(n => (
              <tr key={n}>
                <td style={s.td}>{n}</td>
                {old.map(x => {
                  const g = k => (x[k].families.find(f => f.name === n) || {}).share;
                  return <td key={x.version} style={{ ...s.num, whiteSpace: 'nowrap' }}>{share(g('up5'))} / {share(g('down5'))}</td>;
                })}
              </tr>))}</tbody>
          </table>
        </div>)}
    </div>
  );
}

function Bar({ f, color = C.meter, width = 70, text }) {
  if (f == null) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
      <span style={{ display: 'inline-block', width, height: '6px', background: C.track, borderRadius: '3px', overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${f * 100}%`, height: '100%', background: color }} /></span>
      <span style={{ fontFamily: "'DM Mono', monospace", minWidth: '34px', textAlign: 'right' }}>{text ?? `${Math.round(f * 100)}%`}</span>
    </span>
  );
}

// Too low to the left (blue), too high to the right (red), from a centre line.
function Bias({ low, high, n }) {
  if (!n) return null;
  const l = low / n, h = high / n, w = 50;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }} title={`too low ${Math.round(l * 100)}% · too high ${Math.round(h * 100)}%`}>
      <span style={{ fontFamily: "'DM Mono', monospace", width: '30px', textAlign: 'right' }}>{Math.round(l * 100)}%</span>
      <span style={{ display: 'inline-flex', width: w * 2 + 2, height: '8px' }}>
        <span style={{ width: w, display: 'flex', justifyContent: 'flex-end' }}><span style={{ width: l * w, background: C.low, borderRadius: '2px 0 0 2px' }} /></span>
        <span style={{ width: '2px', background: 'var(--text-muted)' }} />
        <span style={{ width: w }}><span style={{ display: 'block', height: '100%', width: h * w, background: C.high, borderRadius: '0 2px 2px 0' }} /></span>
      </span>
      <span style={{ fontFamily: "'DM Mono', monospace", width: '30px' }}>{Math.round(h * 100)}%</span>
    </span>
  );
}

// Colour swatch + label, for legends and tier names.
const Swatch = ({ k, children }) => (
  <span style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', whiteSpace: 'nowrap' }}>
    <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: C[k], flex: 'none' }} />{children}</span>
);

// Doughnut: segments [[colourKey, label, count]], big text in the middle.
function Donut({ segs, centre, caption }) {
  const n = segs.reduce((a, x) => a + x[2], 0), R = 54, W = 18, L = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div style={{ display: 'flex', gap: '18px', alignItems: 'center', flexWrap: 'wrap' }}>
      <svg viewBox="0 0 140 140" width="140" height="140" role="img" aria-label={caption} style={{ flex: 'none' }}>
        <circle cx="70" cy="70" r={R} fill="none" stroke={C.track} strokeWidth={W} />
        {n > 0 && segs.map(([k, label, c]) => {
          const len = (c / n) * L, el = c ? (
            <circle key={k} cx="70" cy="70" r={R} fill="none" stroke={C[k]} strokeWidth={W} transform="rotate(-90 70 70)"
                    strokeDasharray={`${Math.max(len - (c < n ? 2 : 0), 0.5)} ${L}`} strokeDashoffset={-acc}>
              <title>{`${label}: ${c} of ${n} (${Math.round((c / n) * 100)}%)`}</title></circle>) : null;
          acc += len; return el;
        })}
        <text x="70" y="70" textAnchor="middle" fontSize="24" fontWeight="600" fill="var(--text)">{centre}</text>
        <text x="70" y="90" textAnchor="middle" fontSize="10" fill="var(--text-muted)">{caption}</text>
      </svg>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto auto', gap: '6px 12px', fontSize: '12px', alignItems: 'center' }}>
        {segs.map(([k, label, c]) => <React.Fragment key={k}>
          <Swatch k={k}>{label}</Swatch>
          <span style={{ ...s.mono, textAlign: 'right' }}>{c}</span>
          <span style={{ ...s.mono, textAlign: 'right', color: 'var(--text-muted)' }}>{pct(c, n)}</span>
        </React.Fragment>)}
      </div>
    </div>
  );
}

// Stacked bars, one per row: rows [[label, rs]], segs [[colourKey, label]], kind(r) -> colourKey.
function Stacks({ rows, segs, kind, width = 56 }) {
  return (
    <div>
      {rows.map(([label, rs]) => {
        const n = rs.length;
        return (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <div style={{ width, ...s.mono, fontSize: '12px', textAlign: 'right' }}>{label}</div>
            <div style={{ flex: 1, display: 'flex', gap: '2px', height: '18px', background: n ? 'transparent' : C.track, borderRadius: '4px', overflow: 'hidden' }}>
              {segs.map(([k, sl]) => {
                const c = rs.filter(r => kind(r) === k).length;
                return c ? <div key={k} title={`${sl}: ${c} of ${n} (${pct(c, n)})`} style={{ flex: c, background: C[k], color: '#fff', fontSize: '11px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>{c / n >= 0.12 ? c : ''}</div> : null;
              })}
            </div>
            <div style={{ width: '36px', ...s.mono, fontSize: '12px', color: 'var(--text-muted)' }}>{n}</div>
          </div>
        );
      })}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px', paddingLeft: width + 10 }}>
        {segs.map(([k, sl]) => <Swatch key={k} k={k}>{sl}</Swatch>)}
      </div>
    </div>
  );
}

// Announcements as a table (cell drill-down, wrong way, missed movers, false alarms).
function RowList({ rows, empty }) {
  if (!rows.length) return <div style={s.muted}>{empty}</div>;
  return (
    <div style={s.scroll}>
      <table style={s.table}>
        <thead><tr>{['Released', 'Ticker', 'Announcement', 'Sig', 'Call', 'Surprise', 'Move', 'Outcome', 'Before entry', 'After entry']
          .map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={`${r.source}-${r.id}-${r.instrument_id}`}>
              <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{r.outcome.d}</td>
              <td style={s.td}><Link style={{ color: 'var(--accent)' }} to={`/company?ticker=${encodeURIComponent(r.ticker)}`}>{r.ticker}</Link>
                <div style={{ ...s.muted, fontSize: '11px' }}>{r.sector}</div></td>
              <td style={s.td}>{r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)' }}>{r.title} ↗</a> : r.title}
                {r.count > 1 && <span style={{ ...s.muted, fontSize: '11px' }}> (+{r.count - 1} more that day)</span>}</td>
              <td style={s.num}>{r.significance}</td>
              <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{r.direction === 'positive' ? '▲ ' : r.direction === 'negative' ? '▼ ' : ''}{r.direction}
                <div style={{ ...s.muted, ...s.mono, fontSize: '11px' }}>{r.call === 'flat' ? `flat (band ${r.expected_move}%)` : `${r.call}%`}</div></td>
              <td style={s.td}>{r.surprise || '-'}</td>
              <td style={s.num}>{fmt(r.move)}</td>
              <td style={s.td}><Swatch k={r.tier}>{TIER_LABEL[r.tier]}</Swatch></td>
              <td style={s.num}>{fmt(r.outcome.pre)}</td>
              <td style={s.num}>{fmt(r.outcome.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Positive / negative calls given under 2%: scored on sign alone, with the average move the way of the call.
function SmallCalls({ rows }) {
  const sets = [['All', rows], ['Positive', rows.filter(r => r.direction === 'positive')], ['Negative', rows.filter(r => r.direction === 'negative')]];
  return (
    <div style={s.scroll}>
      <table style={s.table}>
        <thead><tr>{[['', 'left'], ['Calls', 'right'], ['Closed the way it said', 'right'], ['Avg move the way of the call', 'right'],
                     ['Avg when right', 'right'], ['Avg when wrong', 'right']].map(([h, a]) => <th key={h} style={{ ...s.th, textAlign: a }}>{h}</th>)}</tr></thead>
        <tbody>
          {sets.map(([label, rs]) => {
            const m = rs.map(r => r.move * SIGNS[r.direction]), win = m.filter(x => x > 0), loss = m.filter(x => x < 0);
            return (
              <tr key={label}>
                <td style={s.td}>{label}</td>
                <td style={s.num}>{rs.length}</td>
                <td style={s.num}><Bar f={rs.length ? win.length / rs.length : null} /></td>
                <td style={s.num}>{fmt(avg(m))}</td>
                <td style={s.num}>{fmt(avg(win))}</td>
                <td style={s.num}>{fmt(avg(loss))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminStats() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [from, setFrom]       = useState(START);
  const [to, setTo]           = useState(iso(new Date()));
  const [data, setData]       = useState(null);
  const [lt, setLt]           = useState(null);          // Logic Tree input importance, newest model first
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [exchange, setExchange] = useState('');
  const [dirs, setDirs]       = useState(DIRS);
  const [minSig, setMinSig]   = useState(1);
  const [perDay, setPerDay]   = useState(true);
  const [mode, setMode]       = useState('total');            // 'total' | 'after'
  const [chips, setChips]     = useState([]);                 // [{dim, value}] drill-down filters
  const [cell, setCell]       = useState(null);               // {call, cols} selected in the matrix (one row)
  const [dim, setDim]         = useState('sector');
  const [minN, setMinN]       = useState(3);

  useEffect(() => { if (user && !user.is_admin) navigate('/dashboard', { replace: true }); }, [user, navigate]);
  useEffect(() => {
    setLoading(true); setErr('');
    api.get(`/admin/stats/rows?from=${from}&to=${to}`)
      .then(r => setData(r.data)).catch(e => setErr(e.response?.data?.error || e.message)).finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    api.get('/admin/stats/logic-tree-inputs').then(r => setLt(r.data.models || [])).catch(() => setLt([]));
  }, []);

  // Rows with a price outcome, scored, filtered, grouped per company per day.
  const rows = useMemo(() => {
    const base = (data?.rows || []).filter(r => r.outcome && r.outcome[mode] != null && !r.outcome.noise && !r.outcome.suspect)
      .map(r => {
        const x = { ...r, move: r.outcome[mode], actual: bandOf(r.outcome[mode]) }, sg = SIGNS[r.direction];
        return { ...x, tier: tier(x), call: sg ? NEG(sg, r.expected_move) : 'flat', col: NEG(x.move < 0 ? -1 : 1, x.actual) };
      })
      .filter(r => (!exchange || r.exchange === exchange) && dirs.includes(r.direction) && r.significance >= minSig)
      .filter(r => chips.every(c => DIMS.find(d => d.key === c.dim).get(r).includes(c.value)));
    if (!perDay) return base.map(r => ({ ...r, count: 1 }));
    const best = {};
    for (const r of base) {
      const k = `${r.instrument_id}|${r.outcome.d}`, b = best[k];
      const rank = x => x.significance * 10 + MOVES.indexOf(x.expected_move);
      best[k] = !b ? { ...r, count: 1 } : rank(r) > rank(b) ? { ...r, count: b.count + 1 } : { ...b, count: b.count + 1 };
    }
    return Object.values(best);
  }, [data, mode, exchange, dirs, minSig, chips, perDay]);

  const sc = useMemo(() => score(rows), [rows]);
  const grid = useMemo(() => {
    const g = {};
    for (const r of rows) {                                   // a cell is one tier, except unchanged closes in a ±<2 cell
      const c = g[`${r.call}|${r.col}`] = g[`${r.call}|${r.col}`] || { n: 0, t: {} };
      c.n += 1; c.t[r.tier] = (c.t[r.tier] || 0) + 1;
    }
    for (const c of Object.values(g)) c.tier = Object.keys(c.t).sort((a, b) => c.t[b] - c.t[a])[0];
    return g;
  }, [rows]);
  const breakdown = useMemo(() => {
    const d = DIMS.find(x => x.key === dim), by = {};
    for (const r of rows) for (const v of d.get(r)) (by[v] = by[v] || []).push(r);
    return Object.entries(by).map(([v, rs]) => ({ v, ...score(rs) })).filter(x => x.n >= minN).sort((a, b) => b.n - a.n);
  }, [rows, dim, minN]);
  const weeks = useMemo(() => {
    const by = {};
    for (const r of rows) (by[weekOf(r.outcome.d)] = by[weekOf(r.outcome.d)] || []).push(r);
    return Object.entries(by).sort().map(([w, rs]) => ({ w, ...score(rs) }));
  }, [rows]);

  const byMove = (a, b) => Math.abs(b.move) - Math.abs(a.move);
  const unit = perDay ? 'company-days' : 'announcements';
  const cellRows = cell ? rows.filter(r => r.call === cell.call && cell.cols.includes(r.col)).sort(byMove) : [];
  const cellN = cell ? rows.filter(r => r.call === cell.call).length : 0;
  const cellText = cell && `${cell.cols.length} cell${cell.cols.length > 1 ? 's' : ''} · ${cellRows.length} ${unit} · `
    + `${pct(cellRows.length, cellN)} of ${cell.call === 'flat' ? 'flat' : `${cell.call}%`} calls · ${pct(cellRows.length, rows.length)} of all`;
  const toggleCell = (g, a) => setCell(cur => !cur || cur.call !== g ? { call: g, cols: [a] }
    : !cur.cols.includes(a) ? { call: g, cols: [...cur.cols, a] }
    : cur.cols.length > 1 ? { call: g, cols: cur.cols.filter(x => x !== a) } : null);
  const wrong = rows.filter(r => r.tier === 'wrong' && Math.abs(r.move) >= 5).sort(byMove);
  const missed = rows.filter(r => BIG.includes(r.actual) && !BIG.includes(r.expected_move)).sort(byMove);
  const alarms = rows.filter(r => BIG.includes(r.expected_move) && Math.abs(r.move) < 5).sort((a, b) => Math.abs(a.move) - Math.abs(b.move));
  const small = rows.filter(r => SIGNS[r.direction] && r.expected_move === '<2');
  const rightWay = rows.filter(r => SIGNS[r.direction] && ['spot', 'close', 'off'].includes(r.tier));
  const noOutcome = (data?.rows || []).filter(r => !r.outcome || r.outcome[mode] == null).length;
  // Logic Tree: every scored announcement in the selection (not grouped per day), judged on the total reaction.
  const scored = useMemo(() => (data?.rows || []).filter(r => r.model && r.outcome && r.outcome.total != null
    && !r.outcome.noise && !r.outcome.suspect && (!exchange || r.exchange === exchange)), [data, exchange]);
  const thresholds = useMemo(() => [10, 15, 20, 25, 30, 40].map(t => {
    const rs = scored.filter(r => r.model.alert >= t);
    return { t, n: rs.length, hit: rs.filter(r => r.outcome.total >= 5).length,
             mean: rs.length ? rs.reduce((a, r) => a + r.outcome.total, 0) / rs.length : null };
  }), [scored]);
  const aiBig = useMemo(() => {
    const rs = scored.filter(r => r.direction === 'positive' && BIG5.includes(r.expected_move));
    return { n: rs.length, hit: rs.filter(r => r.outcome.total >= 5).length,
             mean: rs.length ? rs.reduce((a, r) => a + r.outcome.total, 0) / rs.length : null };
  }, [scored]);
  const unscored = (data?.rows || []).filter(r => r.outcome && (r.outcome.noise || r.outcome.suspect)).length;  // ASX tick noise, splits, no trades

  const addChip = (d, value) => { if (!chips.some(c => c.dim === d && c.value === value)) setChips([...chips, { dim: d, value }]); setCell(null); };
  const toggleDir = d => setDirs(ds => ds.includes(d) ? ds.filter(x => x !== d) : [...ds, d]);
  const pick = (val, cur, set, label) => <button style={{ ...s.btn, ...(cur === val ? s.on : {}) }} onClick={() => set(val)}>{label}</button>;
  const sizeKind = r => { const d = MOVES.indexOf(r.expected_move) - MOVES.indexOf(r.actual);
    return d <= -2 ? 'low2' : d === -1 ? 'low' : d === 0 ? 'right' : d === 1 ? 'high' : 'high2'; };

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />
      <div style={s.content}>
        <div style={s.heading}>AI Stats</div>
        <p style={s.sub}>
          How the new-system ratings turned out against the price, for every summary with an expected move - audited or not.
          Direction and band are judged together as one call: positive 10-20% means up 10 to 20%; mixed and neutral mean it stays
          within 2% either way. Direction comes first - any move the right way beats a move the wrong way. A positive or negative call
          under 2% is judged on which way it closed. The move runs from the last price before release (the previous close when released
          outside regular hours) to the close of the first session after it, pre-market, after-hours and the ASX auction included.
          "After entry" is what a trade at the AI Audit page's entry got.
        </p>

        <div style={s.section}>
          <div style={s.filters}>
            <div><div style={s.label}>From</div><input type="date" style={s.input} value={from} min={START} onChange={e => setFrom(e.target.value)} /></div>
            <div><div style={s.label}>To</div><input type="date" style={s.input} value={to} onChange={e => setTo(e.target.value)} /></div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button style={s.btn} onClick={() => { setFrom(daysAgo(7) > START ? daysAgo(7) : START); setTo(iso(new Date())); }}>7 days</button>
              <button style={s.btn} onClick={() => { setFrom(daysAgo(30) > START ? daysAgo(30) : START); setTo(iso(new Date())); }}>30 days</button>
              <button style={s.btn} onClick={() => { setFrom(START); setTo(iso(new Date())); }}>Since new system</button>
            </div>
            <div><div style={s.label}>Exchange</div>
              <select style={s.input} value={exchange} onChange={e => setExchange(e.target.value)}>
                <option value="">All</option><option value="AU">ASX</option><option value="US">US</option>
              </select></div>
            <div><div style={s.label}>Direction</div>
              <div style={{ display: 'flex', gap: '10px', fontSize: '13px' }}>
                {DIRS.map(d => <label key={d} style={{ display: 'flex', gap: '4px', alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={dirs.includes(d)} onChange={() => toggleDir(d)} />{d}</label>)}
              </div></div>
            <div><div style={s.label}>Importance</div>
              <select style={s.input} value={minSig} onChange={e => setMinSig(+e.target.value)}>
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n === 1 ? 'All' : `${n} and above`}</option>)}
              </select></div>
            <div><div style={s.label}>Count</div>
              <div style={{ display: 'flex', gap: '4px' }}>{pick(true, perDay, setPerDay, 'Per company per day')}{pick(false, perDay, setPerDay, 'Every announcement')}</div></div>
            <div><div style={s.label}>Move</div>
              <div style={{ display: 'flex', gap: '4px' }}>{pick('total', mode, setMode, 'Total reaction')}{pick('after', mode, setMode, 'After entry')}</div></div>
          </div>
          {chips.length > 0 && <div style={{ marginTop: '12px' }}>
            {chips.map(c => <span key={c.dim + c.value} style={s.chip} title="Remove" onClick={() => setChips(chips.filter(x => x !== c))}>
              {DIMS.find(d => d.key === c.dim).label}: {c.value} ✕</span>)}
            <span style={{ ...s.link, fontSize: '12px' }} onClick={() => setChips([])}>clear all</span></div>}
          <div style={{ ...s.muted, fontSize: '12px', marginTop: '10px' }}>
            {loading ? 'Loading…' : `${rows.length} ${unit}`
              + (noOutcome ? ` · ${noOutcome} announcements not measured yet (left out)` : '')
              + (unscored ? ` · ${unscored} noise or suspect (left out)` : '') + (data?.more ? ' · newest 20,000 loaded - narrow the dates' : '')}
          </div>
          {err && <div style={s.err}>✕ {err}</div>}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px' }}>
          <div style={{ ...s.section, flex: '1 1 420px', marginBottom: 0 }}>
            <div style={s.title}>How did the AI do?</div>
            <div style={{ ...s.muted, fontSize: '12px', marginBottom: '12px' }}>Every call, best outcome to worst · hover a segment for its count</div>
            <Donut segs={TIERS.map(([k, l]) => [k, l, sc.t[k]])} centre={sc.points == null ? '-' : Math.round(sc.points)} caption="score / 100" />
          </div>
          <div style={{ ...s.section, flex: '1 1 420px', marginBottom: 0 }}>
            <div style={s.title}>Did it pick the direction?</div>
            <div style={{ ...s.muted, fontSize: '12px', marginBottom: '12px' }}>Positive and negative calls · 2%+ calls that stayed flat left out</div>
            <Donut segs={[['spot', 'Right way', sc.calledRight], ['wrong', 'Wrong way', sc.called - sc.calledRight]]}
                   centre={pct(sc.calledRight, sc.called)} caption="right way" />
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
          <Tile label="Score" value={sc.points || 0} of={sc.n ? 100 : 0} show={sc.points == null ? '-' : `${Math.round(sc.points)} / 100`}
                sub={`spot on 100 · one band off 75 · 2+ off 50 · flat 25 · wrong way 0 · ${sc.n} ${unit}`} />
          <Tile label="Direction right" value={sc.calledRight} of={sc.called} sub={`${sc.called} positive / negative calls · on big movers ${pct(sc.movedDir, sc.moved)}`} />
          <Tile label="Spot on (way and band)" value={sc.t.spot} of={sc.n} sub={`right band ignoring direction ${pct(sc.right, sc.n)} · within one band ${pct(sc.near, sc.n)}`} />
          <Tile label="Wrong way" value={sc.t.wrong} of={sc.n} sub={`${sc.t.wrong} moved against the call · stayed flat ${pct(sc.t.flat, sc.n)}`} />
          <Tile label="Said 10%+ and it moved 10%+" value={sc.saidHit} of={sc.said} sub={`${sc.said} calls · moved 5%+ ${pct(sc.saidFive, sc.said)} · under 2% ${pct(sc.saidFlat, sc.said)}`} />
          <Tile label="Big movers caught (called 10%+)" value={sc.movedCaught} of={sc.moved} sub={`${sc.moved} moved 10%+ · called 5%+ ${pct(sc.movedFive, sc.moved)} · called under 2% ${pct(sc.movedLow, sc.moved)}`} />
        </div>

        <div style={s.section}>
          <div style={s.title}>The call vs what it did</div>
          <div style={{ ...s.muted, fontSize: '12px', marginBottom: '10px' }}>
            Rows = the call (direction + band; mixed / neutral = flat) · columns = the move · shaded by share of each row, red = went the wrong way · outlined = spot on · click cells in one row to add them up and list them</div>
          <div style={s.scroll}>
            <table style={{ borderCollapse: 'separate', borderSpacing: '2px', fontSize: '12px' }}>
              <thead><tr><th style={{ ...s.th, borderBottom: 'none' }}>Call ↓ · moved →</th>
                {COLS.map(m => <th key={m} style={{ ...s.th, borderBottom: 'none', textAlign: 'center', padding: '6px 2px' }}>{m}%</th>)}
                <th style={{ ...s.th, borderBottom: 'none', textAlign: 'center' }}>Rows</th></tr></thead>
              <tbody>
                {CALLS.map(g => {
                  const n = rows.filter(r => r.call === g).length;
                  return (
                    <tr key={g}>
                      <td style={{ padding: '4px 8px', ...s.mono, textAlign: 'right', whiteSpace: 'nowrap' }}>{g === 'flat' ? 'flat (mixed / neutral)' : `${g}%`}</td>
                      {COLS.map(a => {
                        const c = grid[`${g}|${a}`], f = c && n ? c.n / n : 0, sel = cell && cell.call === g && cell.cols.includes(a);
                        return (
                          <td key={a} title={c ? `Call ${g}, moved ${a}%: ${c.n} of ${n} (${Math.round(f * 100)}%) · ${Object.entries(c.t).map(([k, v]) => `${TIER_LABEL[k]} ${v}`).join(', ')}` : ''}
                              onClick={() => c && toggleCell(g, a)}
                              style={{ minWidth: '54px', height: '42px', textAlign: 'center', borderRadius: '6px', cursor: c ? 'pointer' : 'default',
                                       background: c ? shade(f, c.tier === 'wrong' ? C.red : C.blue) : 'var(--bg)', color: f >= 0.5 ? '#fff' : 'var(--text)',
                                       outline: sel ? '2px solid var(--accent)' : c?.tier === 'spot' ? '2px solid var(--text)' : 'none', outlineOffset: '-2px' }}>
                            {c ? <>
                              <div style={{ fontSize: '14px', fontWeight: 600 }}>{c.n}</div>
                              <div style={{ fontSize: '10px', opacity: 0.85 }}>{Math.round(f * 100)}%</div>
                            </> : <span style={{ color: 'var(--border)' }}>·</span>}
                          </td>
                        );
                      })}
                      <td style={{ padding: '4px 8px', ...s.mono, color: 'var(--text-muted)', textAlign: 'center' }}>{n}</td>
                    </tr>
                  );
                })}
                <tr><td style={{ padding: '6px 8px', color: 'var(--text-muted)', textAlign: 'right' }}>Rows</td>
                  {COLS.map(a => <td key={a} style={{ textAlign: 'center', ...s.mono, color: 'var(--text-muted)' }}>{rows.filter(r => r.col === a).length}</td>)}
                  <td style={{ textAlign: 'center', ...s.mono, color: 'var(--text-muted)' }}>{rows.length}</td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px' }}>
            share of row 0% {C.blue.map(c => <span key={c} style={{ width: '16px', height: '10px', background: c, borderRadius: '2px' }} />)} 100%
            <span style={{ marginLeft: '12px' }}>wrong way 0%</span> {C.red.map(c => <span key={c} style={{ width: '16px', height: '10px', background: c, borderRadius: '2px' }} />)} 100%
          </div>
          {cell && <div style={{ fontSize: '13px', marginTop: '10px' }}><b>Selected:</b> {cellText}
            <span style={{ ...s.link, fontSize: '12px', marginLeft: '10px' }} onClick={() => setCell(null)}>clear</span></div>}
        </div>

        {cell && <div style={s.section}>
          <div style={s.title}>Call {cell.call === 'flat' ? 'flat' : `${cell.call}%`}, moved {COLS.filter(a => cell.cols.includes(a)).map(a => `${a}%`).join(', ')}
            <span style={{ ...s.muted, fontWeight: 400, fontSize: '12px', marginLeft: '8px' }}>{cellText}</span>
            <span style={{ ...s.link, fontWeight: 400, fontSize: '12px', marginLeft: '8px' }} onClick={() => setCell(null)}>clear</span></div>
          <RowList rows={cellRows} empty="None." />
        </div>}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px' }}>
          <div style={{ ...s.section, flex: '1 1 560px', marginBottom: 0 }}>
            <div style={s.title}>Outcome by call</div>
            <div style={{ ...s.muted, fontSize: '12px', marginBottom: '14px' }}>The call (left) · count of rows (right)</div>
            <Stacks rows={CALLS.map(g => [g === 'flat' ? 'flat' : `${g}%`, rows.filter(r => r.call === g)])} segs={TIERS} kind={r => r.tier} width={64} />
          </div>
          <div style={{ ...s.section, flex: '1 1 380px', marginBottom: 0 }}>
            <div style={s.title}>Size check - calls that went the right way</div>
            <div style={{ ...s.muted, fontSize: '12px', marginBottom: '14px' }}>Band given (left) · was the band too low or too high?</div>
            <Stacks rows={MOVES.map(b => [`${b}%`, rightWay.filter(r => r.expected_move === b)])}
                    segs={[['low2', 'Too low, 2+ bands'], ['low', 'Too low'], ['right', 'Right band'], ['high', 'Too high'], ['high2', 'Too high, 2+ bands']]} kind={sizeKind} />
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px' }}>
          <div style={{ ...s.section, flex: '1 1 560px', marginBottom: 0 }}>
            <div style={s.title}>Small calls - positive / negative, under 2% ({small.length})</div>
            <div style={{ ...s.muted, fontSize: '12px', marginBottom: '10px' }}>Judged on which way it closed · moves shown the way of the call (+ = went the way it said)</div>
            <SmallCalls rows={small} />
          </div>
          <div style={{ ...s.section, flex: '1 1 380px', marginBottom: 0 }}>
            <div style={s.title}>By week</div>
            <div style={{ ...s.muted, fontSize: '12px', marginBottom: '10px' }}>Week starting Monday · shows whether prompt changes are helping</div>
            <div style={s.scroll}>
              <table style={s.table}>
                <thead><tr>{[['Week', 'left'], ['Rows', 'right'], ['Score', 'right'], ['Direction right', 'right']]
                  .map(([h, a]) => <th key={h} style={{ ...s.th, textAlign: a }}>{h}</th>)}</tr></thead>
                <tbody>
                  {weeks.map(w => (
                    <tr key={w.w}>
                      <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{w.w}</td>
                      <td style={s.num}>{w.n}</td>
                      <td style={s.num}><Bar f={w.points == null ? null : w.points / 100} text={w.points == null ? '-' : Math.round(w.points)} /></td>
                      <td style={s.num}><Bar f={w.called ? w.calledRight / w.called : null} /></td>
                    </tr>
                  ))}
                  {!weeks.length && <tr><td style={{ ...s.td, ...s.muted }} colSpan={4}>No rows.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div style={s.section}>
          <div style={{ ...s.filters, marginBottom: '10px' }}>
            <div style={s.title}>Breakdown by</div>
            <select style={s.input} value={dim} onChange={e => setDim(e.target.value)}>
              {DIMS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <div style={{ fontSize: '12px', ...s.muted }}>at least <input style={{ ...s.input, width: '50px' }} type="number" min="1" value={minN}
              onChange={e => setMinN(Math.max(1, +e.target.value || 1))} /> rows · click a row to drill in</div>
          </div>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead><tr>{[['', 'left'], ['Rows', 'right'], ['Score', 'right'], ['Direction right', 'right'], ['Wrong way', 'right'], ['Right band', 'right'],
                           ['Too low · too high', 'center'], ['10%+ calls that hit', 'right'], ['Big movers caught', 'right']]
                .map(([h, a]) => <th key={h} style={{ ...s.th, textAlign: a }}>{h}</th>)}</tr></thead>
              <tbody>
                {breakdown.map(b => (
                  <tr key={b.v} style={{ cursor: 'pointer' }} onClick={() => addChip(dim, b.v)}>
                    <td style={{ ...s.td, color: 'var(--accent)' }}>{b.v}</td>
                    <td style={s.num}>{b.n}</td>
                    <td style={s.num}><Bar f={b.points == null ? null : b.points / 100} text={b.points == null ? '-' : Math.round(b.points)} /></td>
                    <td style={s.num}><Bar f={b.called ? b.calledRight / b.called : null} /><div style={{ ...s.muted, fontSize: '10px' }}>{b.called} calls</div></td>
                    <td style={s.num}><Bar f={b.n ? b.t.wrong / b.n : null} color={C.wrong} /></td>
                    <td style={s.num}><Bar f={b.n ? b.right / b.n : null} /></td>
                    <td style={{ ...s.td, textAlign: 'center' }}><Bias low={b.low} high={b.high} n={b.n} /></td>
                    <td style={s.num}><Bar f={b.said ? b.saidHit / b.said : null} /><div style={{ ...s.muted, fontSize: '10px' }}>{b.said} calls</div></td>
                    <td style={s.num}><Bar f={b.moved ? b.movedCaught / b.moved : null} /><div style={{ ...s.muted, fontSize: '10px' }}>{b.moved} movers</div></td>
                  </tr>
                ))}
                {!breakdown.length && <tr><td style={{ ...s.td, ...s.muted }} colSpan={9}>Nothing with that many rows.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div style={s.section}>
          <div style={s.title}>Logic Tree - chance of a 5%+ rise, on the {scored.length} scored announcements in this date range</div>
          <div style={{ ...s.muted, fontSize: '12px', marginBottom: '10px' }}>
            Each line: announcements whose rise chance (▲) was at least that much, how many rose 5%+, the mean total reaction. The chances are
            calibrated, so about that share should rise 5%+ (an average announcement is about 10%). Noise and suspect outcomes left out; the
            exchange filter applies, the others don't. The last line is the AI's own "Positive with a band of 5%+" for comparison.
          </div>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead><tr>{[['Rise chance ▲', 'left'], ['Announcements', 'right'], ['Rose 5%+', 'right'], ['Mean reaction', 'right']]
                .map(([h, a]) => <th key={h} style={{ ...s.th, textAlign: a }}>{h}</th>)}</tr></thead>
              <tbody>
                {thresholds.map(x => (
                  <tr key={x.t}>
                    <td style={s.td}>≥ {x.t}%</td>
                    <td style={s.num}>{x.n}</td>
                    <td style={s.num}><Bar f={x.n ? x.hit / x.n : null} text={x.n ? `${Math.round(100 * x.hit / x.n)}%` : '-'} /></td>
                    <td style={s.num}>{x.mean == null ? '-' : `${x.mean >= 0 ? '+' : ''}${x.mean.toFixed(1)}%`}</td>
                  </tr>
                ))}
                <tr>
                  <td style={s.td}>AI Positive, band 5%+</td>
                  <td style={s.num}>{aiBig.n}</td>
                  <td style={s.num}><Bar f={aiBig.n ? aiBig.hit / aiBig.n : null} text={aiBig.n ? `${Math.round(100 * aiBig.hit / aiBig.n)}%` : '-'} /></td>
                  <td style={s.num}>{aiBig.mean == null ? '-' : `${aiBig.mean >= 0 ? '+' : ''}${aiBig.mean.toFixed(1)}%`}</td>
                </tr>
                {!scored.length && <tr><td style={{ ...s.td, ...s.muted }} colSpan={4}>No scored announcements in this range yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <LogicTreeInputs models={lt} />
        <div style={s.section}>
          <div style={s.title}>Wrong way - moved 5%+ against a positive / negative call ({wrong.length})</div>
          <RowList rows={wrong} empty="None in this selection." />
        </div>
        <div style={s.section}>
          <div style={s.title}>Missed movers - moved 10%+ but given a band under 10% ({missed.length})</div>
          <RowList rows={missed} empty="None in this selection." />
        </div>
        <div style={s.section}>
          <div style={s.title}>False alarms - given 10%+ but moved under 5% ({alarms.length})</div>
          <RowList rows={alarms} empty="None in this selection." />
        </div>
      </div>
    </div>
  );
}
